import React, { useEffect, useMemo, useRef } from 'react'
import { GreetingStep } from './GreetingStep'
import { ExpandableSection } from '../../components/ExpandableSection/ExpandableSection'
import { useMediaQuery } from '../../hooks/useMediaQuery'
import '../../styles/ConciergeModule/GreetingSteps.scss'
import { trackEvent } from '../../../utils/tracking/mixpanel'
import { trackOptimizelyEvent } from '../../../utils/tracking/optimizely'
import { useMatchScreen } from '../../../context/MatchScreenContext'

interface GreetingStepsProps {
  steps: string[]
  toggleText: string
}

export const GreetingSteps = ({ steps, toggleText }: GreetingStepsProps) => {
  const { state: { pageKey } } = useMatchScreen()

  // When the viewport crosses the mobile breakpoint, the accordion content transitions
  // from grid-template-rows: 0fr (mobile, collapsed) to 1fr (desktop, always visible)
  // and vice versa. Without this, the CSS transition fires and the content animates
  // open/closed on resize. We suppress the transition by setting data-resizing on <html>
  // for 300ms after the breakpoint is crossed.
  const mobileQuery = useMemo(() => {
    if (typeof document === 'undefined') return '(max-width: 0px)'
    const mobileWidth = getComputedStyle(document.documentElement).getPropertyValue('--mobile-width').trim()
    return `(max-width: ${mobileWidth})`
  }, [])
  const isMobile = useMediaQuery(mobileQuery)
  const isMobileRef = useRef(isMobile)

  useEffect(() => {
    if (isMobile === isMobileRef.current) return
    isMobileRef.current = isMobile
    document.documentElement.setAttribute('data-resizing', '')
    const timeout = setTimeout(() => {
      document.documentElement.removeAttribute('data-resizing')
    }, 300)
    return () => clearTimeout(timeout)
  }, [isMobile])

  const handleToggle = (expanded: boolean) => {
    const eventKey = 'mobile-concierge-accordion-click'
    const eventPayload = { page_key: pageKey, action: expanded ? 'open' : 'close' }
    trackOptimizelyEvent(eventKey, eventPayload)
    trackEvent('MatchScreen', eventKey, eventPayload)
  }

  return (
    <ExpandableSection label={toggleText} variant='push' className='greeting-steps' onToggle={handleToggle}
                       listClassName='steps-list' toggleClassName='steps-toggle'>
      {steps.map((step, i) => <GreetingStep key={i} step={step} index={i + 1} />)}
    </ExpandableSection>
  )
}
